/**
 * AdminSellerBilling — edit Portalwiz's own billing entity.
 *
 * The seller_billing settings doc is baked into every invoice PDF (name,
 * GSTIN, address, footer). GST tax breakdown ONLY renders when this doc
 * has a GSTIN set — so this page is a prerequisite for GST-compliant
 * invoicing.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, Building2 } from "lucide-react";

const FIELDS = [
    ["name", "Company name", "Portalwiz Talent Solutions Pvt. Ltd."],
    ["gstin", "GSTIN", "29ABCDE1234F1Z5"],
    ["address_line1", "Address line 1", "123 MG Road"],
    ["address_line2", "Address line 2", "Suite 400"],
    ["city", "City", "Bangalore"],
    ["state", "State", "Karnataka"],
    ["pincode", "PIN code", "560001"],
    ["country", "Country", "India"],
    ["email", "Billing email", "billing@portalwiz.com"],
];

export default function AdminSellerBilling() {
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [footer, setFooter] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get("/invoices/admin/seller-billing")
            .then(({ data }) => { setForm(data || {}); setFooter(data?.footer || ""); })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, []);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function save() {
        setSaving(true);
        try {
            const body = { ...form, footer };
            const { data } = await api.put("/invoices/admin/seller-billing", body);
            setForm(data);
            toast.success("Seller billing entity saved");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    if (loading) return <div className="p-6 text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;

    return (
        <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="admin-seller-billing-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight flex items-center gap-2">
                    <Building2 className="w-7 h-7" /> Seller billing entity
                </h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Portalwiz&apos;s name, GSTIN and address that print on every invoice + receipt.
                    Set the GSTIN to unlock the CGST/SGST/IGST breakdown on invoices.
                </p>
            </header>

            <div className="space-y-3 border border-border bg-white p-4">
                {FIELDS.map(([k, label, placeholder]) => (
                    <label key={k} className="block">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
                        <input
                            type="text"
                            value={form[k] || ""}
                            onChange={(e) => setField(k, e.target.value)}
                            placeholder={placeholder}
                            className="mt-1 h-9 w-full border border-border bg-white text-sm px-2"
                            data-testid={`asb-${k}`}
                        />
                    </label>
                ))}
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Footer line (optional)</span>
                    <textarea
                        value={footer}
                        onChange={(e) => setFooter(e.target.value)}
                        rows={2}
                        placeholder="Thank you for choosing Portalwiz. Payments via NEFT/UPI to account XXXX-YYYY."
                        className="mt-1 w-full border border-border bg-white text-sm px-2 py-1.5 resize-none"
                        data-testid="asb-footer"
                    />
                </label>
                <div className="pt-2">
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="h-9 px-4 bg-ikb text-white text-[11px] font-mono uppercase tracking-widest hover:bg-ikb/90 inline-flex items-center gap-1.5 disabled:opacity-60"
                        data-testid="asb-save"
                    >
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
}
